import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Tag,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Tooltip,
  Divider,
  Icon
} from 'antd';
import moment from 'moment';
import {
  getAllEquipment,
  saveEquipment,
  deleteEquipment,
  recordService,
  updateMeterReading,
  updateDailyUsage,
  calculateNextServiceDue,
  clearAllEquipment
} from './utils/maintenanceDB';
import { loadScheduleCsv } from './utils/scheduleCsvLoader';
import { loadAndEnrichEquipment } from './utils/assetListLoader';

const { Option } = Select;
const { TextArea } = Input;

function ScheduleMaintenanceTracker({ form: mainForm }) {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [serviceModalVisible, setServiceModalVisible] = useState(false);
  const [meterModalVisible, setMeterModalVisible] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'overdue', 'due_soon', 'on_track'
  const [assetTypeFilter, setAssetTypeFilter] = useState(null);
  const [assetSubTypeFilter, setAssetSubTypeFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [subLocationFilter, setSubLocationFilter] = useState(null);

  useEffect(() => {
    loadEquipmentFromCsv();
  }, []);

  const loadEquipmentFromCsv = async () => {
    setLoading(true);
    try {
      // Clear all existing equipment first
      clearAllEquipment();
      
      // Load from CSV file
      try {
        let mappedEquipment = await loadScheduleCsv('/Schedule Service Tracker.csv');
        
        // Enrich with asset data from Asset_List.csv
        try {
          mappedEquipment = await loadAndEnrichEquipment(mappedEquipment);
        } catch (assetError) {
          console.warn('Could not load asset list, continuing without asset details:', assetError);
        }
        
        if (mappedEquipment.length > 0) {
          // Save all equipment from CSV to database
          mappedEquipment.forEach(eq => {
            saveEquipment(eq);
          });
          message.success(`Loaded ${mappedEquipment.length} equipment from CSV file`);
        } else {
          message.warning('No equipment found in CSV file');
        }
      } catch (csvError) {
        console.error('Could not load CSV file:', csvError);
        message.error('Failed to load CSV file');
      }
      
      // Load equipment from database
      const allEquipment = getAllEquipment();
      setEquipment(allEquipment);
    } catch (error) {
      console.error('Error loading equipment:', error);
      message.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };

  const loadEquipment = () => {
    setLoading(true);
    try {
      const allEquipment = getAllEquipment();
      setEquipment(allEquipment);
    } catch (error) {
      message.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromCsv = async () => {
    setLoading(true);
    try {
      // Clear all existing equipment first
      clearAllEquipment();
      
      let mappedEquipment = await loadScheduleCsv('/Schedule Service Tracker.csv');
      
      // Enrich with asset data from Asset_List.csv
      try {
        mappedEquipment = await loadAndEnrichEquipment(mappedEquipment);
      } catch (assetError) {
        console.warn('Could not load asset list, continuing without asset details:', assetError);
      }
      
      if (mappedEquipment.length > 0) {
        // Save all equipment from CSV to database
        mappedEquipment.forEach(eq => {
          saveEquipment(eq);
        });
        message.success(`Imported ${mappedEquipment.length} equipment from CSV file`);
        loadEquipment();
      } else {
        message.warning('No data found in CSV file');
      }
    } catch (error) {
      console.error('Error importing from CSV:', error);
      message.error('Failed to import from CSV file');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipment = () => {
    setSelectedEquipment(null);
    mainForm.resetFields();
    setModalVisible(true);
  };

  const handleEditEquipment = (record) => {
    setSelectedEquipment(record);
    mainForm.setFieldsValue({
      ...record,
      lastServiceDate: record.lastServiceDate ? moment(record.lastServiceDate) : null
    });
    setModalVisible(true);
  };

  const handleDeleteEquipment = (id) => {
    if (deleteEquipment(id)) {
      message.success('Equipment deleted successfully');
      loadEquipment();
    } else {
      message.error('Failed to delete equipment');
    }
  };

  const handleSaveEquipment = () => {
    mainForm.validateFields((err, values) => {
      if (!err) {
        const equipmentData = {
          ...values,
          id: selectedEquipment?.id,
          lastServiceDate: values.lastServiceDate ? values.lastServiceDate.toISOString() : null,
          currentMeterReading: values.currentMeterReading || 0,
          lastServiceMeterReading: values.lastServiceMeterReading || values.currentMeterReading || 0,
          dailyUsage: values.dailyUsage || 0
        };
        
        saveEquipment(equipmentData);
        message.success(selectedEquipment ? 'Equipment updated successfully' : 'Equipment added successfully');
        setModalVisible(false);
        loadEquipment();
      }
    });
  };

  const serviceFormRef = React.useRef(null);
  const meterFormRef = React.useRef(null);

  const handleStartService = (record) => {
    setSelectedEquipment(record);
    setServiceModalVisible(true);
    // Set form values after modal opens
    setTimeout(() => {
      if (serviceFormRef.current && serviceFormRef.current.props) {
        const form = serviceFormRef.current.props.form;
        form.setFieldsValue({
          meterReading: record.currentMeterReading || record.lastServiceMeterReading || 0,
          notes: ''
        });
      }
    }, 100);
  };

  const handleCompleteService = () => {
    if (serviceFormRef.current && serviceFormRef.current.props) {
      const form = serviceFormRef.current.props.form;
      form.validateFields((err, values) => {
        if (!err) {
          recordService(selectedEquipment.id, {
            meterReading: values.meterReading,
            notes: values.notes
          });
          message.success('Service recorded successfully');
          setServiceModalVisible(false);
          loadEquipment();
        }
      });
    }
  };

  const handleUpdateMeter = (record) => {
    setSelectedEquipment(record);
    setMeterModalVisible(true);
    // Set form values after modal opens
    setTimeout(() => {
      if (meterFormRef.current && meterFormRef.current.props) {
        const form = meterFormRef.current.props.form;
        form.setFieldsValue({
          meterReading: record.currentMeterReading || 0,
          dailyUsage: record.dailyUsage || 0
        });
      }
    }, 100);
  };

  const handleSaveMeter = () => {
    if (meterFormRef.current && meterFormRef.current.props) {
      const form = meterFormRef.current.props.form;
      form.validateFields((err, values) => {
        if (!err) {
          updateMeterReading(selectedEquipment.id, values.meterReading);
          if (values.dailyUsage !== undefined) {
            updateDailyUsage(selectedEquipment.id, values.dailyUsage);
          }
          message.success('Meter reading updated successfully');
          setMeterModalVisible(false);
          loadEquipment();
        }
      });
    }
  };

  const getStatusTag = (equipment) => {
    const serviceInfo = calculateNextServiceDue(equipment);
    const status = serviceInfo.status;
    
    if (status === 'overdue') {
      return <Tag color="red" icon={<Icon type="exclamation-circle" />}>Overdue</Tag>;
    } else if (status === 'due') {
      return <Tag color="orange" icon={<Icon type="clock-circle" />}>Due Now</Tag>;
    } else if (status === 'due_soon') {
      return <Tag color="gold" icon={<Icon type="clock-circle" />}>Due Soon</Tag>;
    } else {
      return <Tag color="green" icon={<Icon type="check-circle" />}>On Track</Tag>;
    }
  };

  const getMeterUnit = (meterType) => {
    const units = {
      kms: 'km',
      hours: 'hrs',
      kwh: 'kWh'
    };
    return units[meterType] || '';
  };

  const columns = [
    {
      title: 'Equipment Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <div>
          <strong>{text}</strong>
          <br />
          <span style={{ fontSize: '12px', color: '#999' }}>{record.id}</span>
        </div>
      )
    },
    {
      title: 'Meter Type',
      dataIndex: 'meterType',
      key: 'meterType',
      width: 100,
      render: (type) => <Tag>{type?.toUpperCase() || 'N/A'}</Tag>
    },
    {
      title: 'Current Reading',
      key: 'currentReading',
      width: 150,
      render: (_, record) => (
        <div>
          <strong>{record.currentMeterReading || 0}</strong>
          <span style={{ marginLeft: 4, color: '#999' }}>
            {getMeterUnit(record.meterType)}
          </span>
        </div>
      )
    },
    {
      title: 'Daily Usage',
      key: 'dailyUsage',
      width: 120,
      render: (_, record) => (
        <div>
          {record.dailyUsage || 0} {getMeterUnit(record.meterType)}/day
        </div>
      )
    },
    {
      title: 'Last Service',
      key: 'lastService',
      width: 150,
      render: (_, record) => (
        <div>
          {record.lastServiceDate ? (
            <>
              <div>{moment(record.lastServiceDate).format('MMM DD, YYYY')}</div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.lastServiceMeterReading || 0} {getMeterUnit(record.meterType)}
              </div>
            </>
          ) : (
            <span style={{ color: '#999' }}>No service recorded</span>
          )}
        </div>
      )
    },
    {
      title: 'Next Service Due',
      key: 'nextService',
      width: 200,
      render: (_, record) => {
        const serviceInfo = calculateNextServiceDue(record);
        return (
          <div>
            {serviceInfo.nextDue ? (
              <>
                <div>
                  <strong>{moment(serviceInfo.nextDue).format('MMM DD, YYYY')}</strong>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {serviceInfo.daysUntilDue !== null && (
                    <>
                      {serviceInfo.daysUntilDue < 0 
                        ? `${Math.abs(serviceInfo.daysUntilDue)} days overdue`
                        : serviceInfo.daysUntilDue === 0
                        ? 'Due today'
                        : `${serviceInfo.daysUntilDue} days remaining`
                      }
                    </>
                  )}
                </div>
                {serviceInfo.timeBased && serviceInfo.meterBased && (
                  <div style={{ fontSize: '11px', color: '#bbb', marginTop: 4 }}>
                    {new Date(serviceInfo.timeBased) < new Date(serviceInfo.nextDue) 
                      ? 'Time-based' 
                      : 'Meter-based'}
                  </div>
                )}
              </>
            ) : (
              <span style={{ color: '#999' }}>Not configured</span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => getStatusTag(record)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Start Service">
            <Button
              type="primary"
              icon="check-circle"
              size="small"
              onClick={() => handleStartService(record)}
            >
              Start Service
            </Button>
          </Tooltip>
          <Tooltip title="Update Meter">
            <Button
              icon="reload"
              size="small"
              onClick={() => handleUpdateMeter(record)}
            >
              Update Meter
            </Button>
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon="edit"
              size="small"
              onClick={() => handleEditEquipment(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this equipment?"
            onConfirm={() => handleDeleteEquipment(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                icon="delete"
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </div>
      )
    }
  ];

  // Filter equipment by status
  const overdueEquipment = equipment.filter(eq => {
    const info = calculateNextServiceDue(eq);
    return info.status === 'overdue';
  });

  const dueSoonEquipment = equipment.filter(eq => {
    const info = calculateNextServiceDue(eq);
    return info.status === 'due' || info.status === 'due_soon';
  });

  const onTrackEquipment = equipment.filter(eq => {
    const info = calculateNextServiceDue(eq);
    return info.status === 'ok';
  });

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    const values = equipment
      .map(eq => eq[field])
      .filter(val => val && val !== null && val !== '')
      .sort();
    return [...new Set(values)];
  };

  const uniqueAssetTypes = getUniqueValues('assetType');
  const uniqueAssetSubTypes = getUniqueValues('assetSubType');
  const uniqueLocations = getUniqueValues('location');
  const uniqueSubLocations = getUniqueValues('subLocation');

  // Filter equipment based on selected filters
  const getFilteredEquipment = () => {
    let filtered = equipment;

    // Status filter
    if (statusFilter === 'overdue') {
      filtered = overdueEquipment;
    } else if (statusFilter === 'due_soon') {
      filtered = dueSoonEquipment;
    } else if (statusFilter === 'on_track') {
      filtered = onTrackEquipment;
    }

    // Asset Type filter
    if (assetTypeFilter) {
      filtered = filtered.filter(eq => eq.assetType === assetTypeFilter);
    }

    // Asset Sub Type filter
    if (assetSubTypeFilter) {
      filtered = filtered.filter(eq => eq.assetSubType === assetSubTypeFilter);
    }

    // Location filter
    if (locationFilter) {
      filtered = filtered.filter(eq => eq.location === locationFilter);
    }

    // Sub Location filter
    if (subLocationFilter) {
      filtered = filtered.filter(eq => eq.subLocation === subLocationFilter);
    }

    return filtered;
  };

  const filteredEquipment = getFilteredEquipment();

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <style>{`
        .filter-active {
          border: 2px solid #1890ff !important;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
        }
        .ant-card {
          transition: all 0.3s;
        }
        .ant-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Card 
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('all')}
              className={statusFilter === 'all' ? 'filter-active' : ''}
            >
              <Statistic
                title="Total Equipment"
                value={equipment.length}
                prefix={<Icon type="tool" />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card 
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('overdue')}
              className={statusFilter === 'overdue' ? 'filter-active' : ''}
            >
              <Statistic
                title="Overdue"
                value={overdueEquipment.length}
                prefix={<Icon type="exclamation-circle" />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card 
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('due_soon')}
              className={statusFilter === 'due_soon' ? 'filter-active' : ''}
            >
              <Statistic
                title="Due Soon"
                value={dueSoonEquipment.length}
                prefix={<Icon type="clock-circle" />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card 
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('on_track')}
              className={statusFilter === 'on_track' ? 'filter-active' : ''}
            >
              <Statistic
                title="On Track"
                value={onTrackEquipment.length}
                prefix={<Icon type="check-circle" />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <Icon type="tool" style={{ marginRight: 8 }} />
              Schedule Maintenance Tracker
            </span>
            <Button
              type="primary"
              icon="plus"
              onClick={handleAddEquipment}
            >
              Add Equipment
            </Button>
          </div>
        }
        extra={
          <div>
            <Button 
              icon="upload" 
              onClick={handleImportFromCsv}
              style={{ marginRight: 8 }}
            >
              Import from CSV
            </Button>
            <Button icon="reload" onClick={loadEquipment}>
              Refresh
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ marginRight: 8, fontWeight: 500 }}>Filter by Status:</span>
              <Button
                type={statusFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('all')}
                size="small"
              >
                All ({equipment.length})
              </Button>
              <Button
                type={statusFilter === 'overdue' ? 'primary' : 'default'}
                danger={statusFilter === 'overdue'}
                onClick={() => setStatusFilter('overdue')}
                size="small"
              >
                Overdue ({overdueEquipment.length})
              </Button>
              <Button
                type={statusFilter === 'due_soon' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('due_soon')}
                size="small"
              >
                Due Soon ({dueSoonEquipment.length})
              </Button>
              <Button
                type={statusFilter === 'on_track' ? 'primary' : 'default'}
                onClick={() => setStatusFilter('on_track')}
                size="small"
              >
                On Track ({onTrackEquipment.length})
              </Button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ marginRight: 8, fontWeight: 500 }}>Filter by Asset:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Asset Type:</span>
              <Select
                placeholder="Select Asset Type"
                allowClear
                value={assetTypeFilter}
                onChange={setAssetTypeFilter}
                style={{ width: 200 }}
                size="small"
              >
                {uniqueAssetTypes.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Asset Sub Type:</span>
              <Select
                placeholder="Select Asset Sub Type"
                allowClear
                value={assetSubTypeFilter}
                onChange={setAssetSubTypeFilter}
                style={{ width: 200 }}
                size="small"
              >
                {uniqueAssetSubTypes.map(subType => (
                  <Option key={subType} value={subType}>{subType}</Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Location:</span>
              <Select
                placeholder="Select Location"
                allowClear
                value={locationFilter}
                onChange={setLocationFilter}
                style={{ width: 150 }}
                size="small"
              >
                {uniqueLocations.map(location => (
                  <Option key={location} value={location}>{location}</Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Sub Location:</span>
              <Select
                placeholder="Select Sub Location"
                allowClear
                value={subLocationFilter}
                onChange={setSubLocationFilter}
                style={{ width: 150 }}
                size="small"
              >
                {uniqueSubLocations.map(subLocation => (
                  <Option key={subLocation} value={subLocation}>{subLocation}</Option>
                ))}
              </Select>
            </div>
            {(assetTypeFilter || assetSubTypeFilter || locationFilter || subLocationFilter) && (
              <Button
                size="small"
                onClick={() => {
                  setAssetTypeFilter(null);
                  setAssetSubTypeFilter(null);
                  setLocationFilter(null);
                  setSubLocationFilter(null);
                }}
              >
                Clear Asset Filters
              </Button>
            )}
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={filteredEquipment}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => {
              if (statusFilter === 'all') {
                return `Total ${total} equipment`;
              } else {
                return `${range[0]}-${range[1]} of ${total} equipment (${statusFilter === 'overdue' ? 'Overdue' : statusFilter === 'due_soon' ? 'Due Soon' : 'On Track'})`;
              }
            }
          }}
        />
      </Card>

      {/* Add/Edit Equipment Modal */}
      <Modal
        title={selectedEquipment ? 'Edit Equipment' : 'Add Equipment'}
        visible={modalVisible}
        onOk={handleSaveEquipment}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <Form.Item label="Equipment Name">
            {mainForm.getFieldDecorator('name', {
              rules: [{ required: true, message: 'Please enter equipment name' }]
            })(
              <Input placeholder="e.g., Vehicle ABC-123" />
            )}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Meter Type">
                {mainForm.getFieldDecorator('meterType', {
                  initialValue: 'kms',
                  rules: [{ required: true, message: 'Please select meter type' }]
                })(
                  <Select>
                    <Option value="kms">Kilometers (kms)</Option>
                    <Option value="hours">Hours</Option>
                    <Option value="kwh">Kilowatt Hours (kWh)</Option>
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Current Meter Reading">
                {mainForm.getFieldDecorator('currentMeterReading', {
                  initialValue: 0,
                  rules: [{ required: true, message: 'Please enter current reading' }]
                })(
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="0"
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Service Interval (Days)">
                {mainForm.getFieldDecorator('serviceIntervalDays', {
                  initialValue: 90,
                  rules: [{ required: true, message: 'Please enter service interval in days' }]
                })(
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="90"
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Service Interval (Meter)">
                {mainForm.getFieldDecorator('serviceIntervalMeter', {
                  initialValue: 10000,
                  rules: [{ required: true, message: 'Please enter service interval in meter units' }]
                })(
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="10000"
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Daily Usage (for meter-based calculation)">
                {mainForm.getFieldDecorator('dailyUsage', {
                  initialValue: 0
                })(
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="0"
                  />
                )}
                <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                  Average daily usage to calculate when meter-based service will be due
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Last Service Date">
                {mainForm.getFieldDecorator('lastServiceDate')(
                  <DatePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Last Service Meter Reading">
            {mainForm.getFieldDecorator('lastServiceMeterReading', {
              initialValue: 0
            })(
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="0"
              />
            )}
          </Form.Item>

          <Form.Item label="Description">
            {mainForm.getFieldDecorator('description')(
              <TextArea rows={3} placeholder="Additional notes about the equipment" />
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Start Service Modal */}
      <ServiceForm
        wrappedComponentRef={serviceFormRef}
        visible={serviceModalVisible}
        onOk={handleCompleteService}
        onCancel={() => setServiceModalVisible(false)}
        selectedEquipment={selectedEquipment}
        getMeterUnit={getMeterUnit}
      />

      {/* Update Meter Modal */}
      <MeterForm
        wrappedComponentRef={meterFormRef}
        visible={meterModalVisible}
        onOk={handleSaveMeter}
        onCancel={() => setMeterModalVisible(false)}
        selectedEquipment={selectedEquipment}
        getMeterUnit={getMeterUnit}
      />
    </div>
  );
}

// Service Form Component
const ServiceFormComponent = ({ form, visible, onOk, onCancel, selectedEquipment, getMeterUnit }) => {
  return (
    <Modal
      title="Record Service"
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      okText="Complete Service"
      cancelText="Cancel"
    >
      <Form layout="vertical">
        <Form.Item label={`Meter Reading at Service (${getMeterUnit(selectedEquipment?.meterType)})`}>
          {form.getFieldDecorator('meterReading', {
            rules: [{ required: true, message: 'Please enter meter reading' }]
          })(
            <InputNumber
              style={{ width: '100%' }}
              min={0}
            />
          )}
        </Form.Item>
        <Form.Item label="Service Notes">
          {form.getFieldDecorator('notes')(
            <TextArea rows={4} placeholder="Enter any notes about the service performed" />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ServiceForm = Form.create()(ServiceFormComponent);

// Meter Form Component
const MeterFormComponent = ({ form, visible, onOk, onCancel, selectedEquipment, getMeterUnit }) => {
  return (
    <Modal
      title="Update Meter Reading"
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      okText="Update"
      cancelText="Cancel"
    >
      <Form layout="vertical">
        <Form.Item label={`Current Meter Reading (${getMeterUnit(selectedEquipment?.meterType)})`}>
          {form.getFieldDecorator('meterReading', {
            rules: [{ required: true, message: 'Please enter meter reading' }]
          })(
            <InputNumber
              style={{ width: '100%' }}
              min={0}
            />
          )}
        </Form.Item>
        <Form.Item label={`Daily Usage (${getMeterUnit(selectedEquipment?.meterType)}/day)`}>
          {form.getFieldDecorator('dailyUsage')(
            <InputNumber
              style={{ width: '100%' }}
              min={0}
            />
          )}
          <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
            Update the average daily usage for better meter-based service predictions
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const MeterForm = Form.create()(MeterFormComponent);

export default Form.create({ name: 'equipment_form' })(ScheduleMaintenanceTracker);

